import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';

import imgLogo from '../../assets/images/logo-login.svg';
import imgIconKakao from '../../assets/images/icon-kakao.svg';
import imgIconNaver from '../../assets/images/icon-naver.svg';
import imgIconApple from '../../assets/images/icon-apple.svg';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Footer from '../../components/Footer';

// Kakao SDK 타입 선언
declare global {
  interface Window {
    Kakao: any;
    naver: any;
  }
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [isApplePlatform, setIsApplePlatform] = useState(false);

  // Apple 플랫폼 감지 (iOS, macOS)
  useEffect(() => {
    const checkApplePlatform = () => {
      // Flutter WebView에서 전달된 플랫폼 확인
      const flutterPlatform = localStorage.getItem('flutter_device_platform');
      if (flutterPlatform === 'ios') {
        return true;
      }

      // 일반 브라우저에서 iOS/macOS 확인
      const ua = navigator.userAgent;
      return /iPad|iPhone|iPod/.test(ua) ||
             (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) || // iPad with desktop mode
             /Macintosh/.test(ua); // macOS Safari
    };

    setIsApplePlatform(checkApplePlatform());
  }, []);

  // 동적 BASE_URL 설정 (환경 변수 우선, 없으면 현재 도메인 사용)
  const getBaseUrl = () => {
    return process.env.REACT_APP_BASE_URL || window.location.origin;
  };

  // 이미 로그인된 사용자는 메인으로 리다이렉트
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      console.log('[Login] 이미 로그인됨, /main으로 이동');
      navigate('/main', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Kakao SDK 초기화
  useEffect(() => {
    const kakaoKey = process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY;
    console.log('Kakao Key:', kakaoKey); // 디버깅용

    if (window.Kakao && !window.Kakao.isInitialized()) {
      if (!kakaoKey) {
        console.error('Kakao JavaScript Key가 설정되지 않았습니다.');
        return;
      }
      window.Kakao.init(kakaoKey);
      console.log('Kakao SDK 초기화 완료:', window.Kakao.isInitialized());
    }
  }, []);

  const handleKakaoLogin = () => {
    if (!window.Kakao) {
      alert('카카오 SDK가 로드되지 않았습니다.');
      return;
    }

    const baseUrl = getBaseUrl();
    const redirectUri = `${baseUrl}/auth/callback/kakao`;

    // 카카오 REST API를 사용한 OAuth 로그인
    // prompt=login: 매번 로그인 화면 표시 (계정 재선택 가능)
    const kakaoAuthUrl = `https://kauth.kakao.com/oauth/authorize?client_id=${process.env.REACT_APP_KAKAO_REST_API_KEY}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&prompt=login`;

    console.log('[Login] 카카오 로그인 시도:', redirectUri);
    window.location.replace(kakaoAuthUrl);
  };

  const handleNaverLogin = () => {
    const naverClientId = process.env.REACT_APP_NAVER_CLIENT_ID;
    const baseUrl = getBaseUrl();
    const redirectUri = `${baseUrl}/auth/callback/naver`;
    const state = Math.random().toString(36).substring(7);

    console.log('[Login] 네이버 로그인 시도:', redirectUri);

    // state 값 저장 (콜백에서 검증용) - WebView 호환을 위해 localStorage 사용
    localStorage.setItem('naver_state', state);

    // 네이버 OAuth 웹 로그인 URL
    const naverAuthUrl = `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${naverClientId}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;

    window.location.replace(naverAuthUrl);
  };

  // Flutter 앱에서 접속했는지 확인
  const isFlutterApp = () => {
    // Flutter WebView에서 저장한 플랫폼 정보 확인
    const flutterPlatform = localStorage.getItem('flutter_device_platform');
    return flutterPlatform === 'ios' || flutterPlatform === 'android';
  };

  const handleGoogleLogin = async () => {
    try {
      // Flutter 앱에서 접속했으면 딥링크 사용, 아니면 웹 콜백 사용
      const redirectTo = isFlutterApp()
        ? 'bbibbi://auth/callback'
        : `${getBaseUrl()}/auth/callback`;

      console.log('[Login] 구글 로그인 시도:', redirectTo, '(Flutter:', isFlutterApp(), ')');

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          skipBrowserRedirect: true  // 자동 리다이렉트 방지
        }
      });

      if (error) {
        console.error('구글 로그인 오류:', error);
        alert('구글 로그인 중 오류가 발생했습니다.');
        return;
      }

      // replace로 이동하여 히스토리에 추가되지 않도록
      if (data?.url) {
        window.location.replace(data.url);
      }
    } catch (error) {
      console.error('구글 로그인 실패:', error);
      alert('구글 로그인에 실패했습니다.');
    }
  };

  const handleAppleLogin = async () => {
    try {
      // Flutter 앱에서 접속했으면 딥링크 사용, 아니면 웹 콜백 사용
      const redirectTo = isFlutterApp()
        ? 'bbibbi://auth/callback'
        : `${getBaseUrl()}/auth/callback`;

      console.log('[Login] Apple 로그인 시도:', redirectTo, '(Flutter:', isFlutterApp(), ')');

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: redirectTo,
          skipBrowserRedirect: true
        }
      });

      if (error) {
        console.error('Apple 로그인 오류:', error);
        alert('Apple 로그인 중 오류가 발생했습니다.');
        return;
      }

      if (data?.url) {
        window.location.replace(data.url);
      }
    } catch (error) {
      console.error('Apple 로그인 실패:', error);
      alert('Apple 로그인에 실패했습니다.');
    }
  };

  return (
    <div className="login-container">
      <div className="login-content">
        <div className="login-inner">
          <div className="login-top">
            <div className="logo-section">
              <div className="logo-wrapper">
                <img src={imgLogo} alt="삐삐 로고" className="logo" />
              </div>
              <h1 className="login-title">
                지금 바로,
                <br />
                시작해 볼까요?
              </h1>
              <p className="login-subtitle">초간단 지원금 가격비교</p>
            </div>
          </div>

          <div className="login-buttons">
            <button className="login-btn kakao-btn" onClick={handleKakaoLogin}>
              <img src={imgIconKakao} alt="" className="btn-icon" />
              <span>Kakao 로그인</span>
            </button>

            <button className="login-btn naver-btn" onClick={handleNaverLogin}>
              <img src={imgIconNaver} alt="" className="btn-icon" />
              <span>Naver 로그인</span>
            </button>

            <button className="login-btn google-btn" onClick={handleGoogleLogin}>
              <svg className="btn-icon google-icon" viewBox="0 0 20 20" fill="none">
                <path d="M18.1712 8.36788H17.5V8.33329H9.99996V11.6666H14.7095C14.0225 13.607 12.1762 15 9.99996 15C7.23871 15 4.99996 12.7612 4.99996 9.99996C4.99996 7.23871 7.23871 4.99996 9.99996 4.99996C11.2745 4.99996 12.4337 5.48079 13.3166 6.26621L15.6741 3.90871C14.1858 2.52204 12.195 1.66663 9.99996 1.66663C5.39788 1.66663 1.66663 5.39788 1.66663 9.99996C1.66663 14.602 5.39788 18.3333 9.99996 18.3333C14.602 18.3333 18.3333 14.602 18.3333 9.99996C18.3333 9.44121 18.2758 8.89579 18.1712 8.36788Z" fill="#FFC107"/>
                <path d="M2.6275 6.12121L5.36542 8.12954C6.10625 6.29537 7.90042 4.99996 10 4.99996C11.2746 4.99996 12.4337 5.48079 13.3167 6.26621L15.6742 3.90871C14.1858 2.52204 12.195 1.66663 10 1.66663C6.79917 1.66663 4.02334 3.47371 2.6275 6.12121Z" fill="#FF3D00"/>
                <path d="M10 18.3333C12.1525 18.3333 14.1084 17.5095 15.5871 16.1712L13.0079 13.9879C12.1431 14.6381 11.0864 15 10 15C7.83251 15 5.99209 13.6179 5.29876 11.6891L2.58209 13.783C3.96043 16.4816 6.76043 18.3333 10 18.3333Z" fill="#4CAF50"/>
                <path d="M18.1713 8.36796H17.5V8.33337H10V11.6667H14.7096C14.3809 12.5902 13.7889 13.3972 13.0067 13.988L13.0079 13.9872L15.5871 16.1705C15.4046 16.3355 18.3334 14.1667 18.3334 10C18.3334 9.44129 18.2759 8.89587 18.1713 8.36796Z" fill="#1976D2"/>
              </svg>
              <span>Google 로그인</span>
            </button>

            {isApplePlatform && (
              <button className="login-btn apple-btn" onClick={handleAppleLogin}>
                <img src={imgIconApple} alt="" className="btn-icon" />
                <span>Apple 로그인</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <Footer
        onTermsClick={() => navigate('/terms')}
        onPrivacyClick={() => navigate('/privacy')}
      />
    </div>
  );
};

export default Login;
